import React, { useState } from 'react';
import { AnalystReview } from '../../../types/monitor.types';
import { formatTimeUTC6, formatDateTimeUTC6, formatFullDateTimeUTC6 } from '../../../utils/formatters';

interface Props {
  reviews: AnalystReview[];
  loading: boolean;
  onRefresh: () => void;
  showDate?: boolean;  // Historical page shows MM/DD HH:MM; live monitor only HH:MM
}

const verdictClass = (verdict: string, failOpen: boolean): string => {
  if (failOpen) return 'verdict-failclose';
  if (verdict === 'BLOCK') return 'verdict-block';
  if (verdict === 'ALLOW') return 'verdict-allow';
  return 'verdict-other';
};

const outcomeClass = (outcome: string | null): string => {
  if (!outcome) return 'outcome-pending';
  if (outcome === 'WIN') return 'outcome-win';
  if (outcome === 'LOSS') return 'outcome-loss';
  if (outcome === 'BREAKEVEN') return 'outcome-be';
  if (outcome === 'BLOCKED') return 'outcome-blocked';
  if (outcome === 'NO_TRADE') return 'outcome-notrade';
  return 'outcome-other';
};

const AnalystReviews: React.FC<Props> = ({ reviews, loading, onRefresh, showDate = false }) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const toggle = (id: string) => setExpandedId(prev => (prev === id ? null : id));

  if (loading && reviews.length === 0) {
    return (
      <div className="analyst-reviews-section">
        <p className="analyst-reviews-empty">Loading analyst reviews...</p>
      </div>
    );
  }

  if (reviews.length === 0) {
    return (
      <div className="analyst-reviews-section">
        <div className="analyst-reviews-header">
          <button className="reviews-refresh-btn" onClick={onRefresh}>↻ Refresh</button>
        </div>
        <p className="analyst-reviews-empty">No analyst reviews yet</p>
      </div>
    );
  }

  return (
    <div className="analyst-reviews-section">
      <div className="analyst-reviews-header">
        <span className="reviews-count">{reviews.length} reviews</span>
        <button className="reviews-refresh-btn" onClick={onRefresh}>↻ Refresh</button>
      </div>
      <div className="analyst-reviews-table-wrap">
        <table className="analyst-reviews-table">
          <thead>
            <tr>
              <th></th>
              <th>Time</th>
              <th>Strategy</th>
              <th>Instrument</th>
              <th>Dir</th>
              <th>Verdict</th>
              <th className="hide-mobile">Conf</th>
              <th className="hide-mobile">Tools</th>
              <th className="hide-mobile">Lat</th>
              <th>Outcome</th>
              <th className="hide-mobile">P/L</th>
            </tr>
          </thead>
          <tbody>
            {reviews.map(r => {
              const expanded = expandedId === r.reviewId;
              const time = showDate ? formatDateTimeUTC6(r.createdAt) : formatTimeUTC6(r.createdAt);
              return (
                <React.Fragment key={r.reviewId}>
                  <tr
                    className={`review-row ${expanded ? 'review-row-expanded' : ''}`}
                    onClick={() => toggle(r.reviewId)}
                  >
                    <td className="td-chevron">{expanded ? '▾' : '▸'}</td>
                    <td className="td-time">{time}</td>
                    <td className="td-model">{r.strategy}</td>
                    <td className="td-mono">{r.instrument}</td>
                    <td>
                      <span className={`dir-tag ${r.direction === 'BUY' ? 'dir-buy' : 'dir-sell'}`}>
                        {r.direction}
                      </span>
                    </td>
                    <td>
                      <span className={`verdict-badge ${verdictClass(r.verdict, r.failOpen)}`}>
                        {r.failOpen ? 'FAIL-CLOSE' : r.verdict}
                      </span>
                    </td>
                    <td className="td-mono hide-mobile">
                      {r.confidence != null ? `${Math.round(r.confidence * 100)}%` : '—'}
                    </td>
                    <td className="td-mono hide-mobile">{r.toolsUsed ?? '—'}</td>
                    <td className="td-mono hide-mobile">
                      {r.latencyMs != null ? `${(r.latencyMs / 1000).toFixed(1)}s` : '—'}
                    </td>
                    <td>
                      <span className={`outcome-badge ${outcomeClass(r.tradeOutcome)}`}>
                        {r.tradeOutcome ?? 'PENDING'}
                      </span>
                    </td>
                    <td className={`td-mono td-pl hide-mobile ${r.pnlUsd != null && r.pnlUsd < 0 ? 'pl-neg' : r.pnlUsd != null && r.pnlUsd > 0 ? 'pl-pos' : ''}`}>
                      {r.pnlUsd != null ? `$${r.pnlUsd.toFixed(2)}` : '—'}
                    </td>
                  </tr>
                  {expanded && (
                    <tr className="review-detail-row">
                      <td colSpan={11}>
                        <div className="review-detail">
                          {r.reasoning && (
                            <div className="review-section">
                              <div className="review-section-title">Reasoning</div>
                              <div className="review-reasoning">{r.reasoning}</div>
                            </div>
                          )}
                          {r.redFlags.length > 0 && (
                            <div className="review-section">
                              <div className="review-section-title review-section-red">
                                Red flags ({r.redFlags.length})
                              </div>
                              <ul className="review-flag-list review-flag-red">
                                {r.redFlags.map((f, i) => <li key={i}>{f}</li>)}
                              </ul>
                            </div>
                          )}
                          {r.greenFlags.length > 0 && (
                            <div className="review-section">
                              <div className="review-section-title review-section-green">
                                Green flags ({r.greenFlags.length})
                              </div>
                              <ul className="review-flag-list review-flag-green">
                                {r.greenFlags.map((f, i) => <li key={i}>{f}</li>)}
                              </ul>
                            </div>
                          )}
                          <div className="review-meta">
                            <span><b>review_id:</b> {r.reviewId}</span>
                            {r.matchedTradeId != null && <span><b>trade_id:</b> {r.matchedTradeId}</span>}
                            <span><b>triggered:</b> {formatFullDateTimeUTC6(r.triggeredAt)}</span>
                            <span><b>model:</b> claude-sonnet-4-6</span>
                            {r.failOpen && <span className="review-failopen-tag">synthetic fallback (analyst unreachable)</span>}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AnalystReviews;
